import React from "react";
import { motion } from "framer-motion";
import { Shield, ExternalLink, ChevronUp, ChevronDown } from "lucide-react";
import axios from "axios";
import DocumentExpandedView from "./DocumentExpandedView";
import DocumentMetaLine from "./DocumentMetaLine";

const ReviewSection = ({
  isDarkMode,
  themeClasses,
  showReviewedDocs,
  setShowReviewedDocs,
  fetchReviewDocuments,
  isLoadingReview,
  reviewDocuments,
  expandedReviewDocs,
  setExpandedReviewDocs,
  markingAsVerified,
  markDocumentAsVerified,
  documentViewModes,
  resultViewMode,
  editingDocuments,
  savingDocuments,
  handleDocumentViewModeChange,
  handleEditDocument,
  handleSaveDocument,
  handleCancelEdit,
  copyToClipboard,
  downloadAsCSV,
  toggleResultViewMode,
  MediaViewer,
  presignedUrls,
  loadingUrls,
  fetchPresignedUrls,
  formatDateTime,
  favicon,
}) => {
  return (
    <div>
      {/* Toggle between Reviewed and Non-Reviewed */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setShowReviewedDocs(false);
                fetchReviewDocuments("needs verification");
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                !showReviewedDocs
                  ? isDarkMode
                    ? "bg-blue-600 text-white"
                    : "bg-blue-500 text-white"
                  : isDarkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600 border-slate-950"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 border-slate-280"
              }`}
            >
              Needs Verification
            </button>
            <button
              onClick={() => {
                setShowReviewedDocs(true);
                fetchReviewDocuments("verified");
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                showReviewedDocs
                  ? isDarkMode
                    ? "bg-green-600 text-white"
                    : "bg-green-500 text-white"
                  : isDarkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600 boder-slate-950"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 border-slate-280"
              }`}
            >
              Verified
            </button>
          </div>
        </div>
      </div>

      {/* Review Documents */}
      {isLoadingReview ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <div className="relative w-7 h-7">
              <motion.div
                className="w-full h-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 2,
                  ease: "linear",
                  repeat: Infinity,
                }}
              >
                <img
                  src={favicon}
                  alt="Loading"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    console.log("Image failed to load:", e.target.src);
                    e.target.style.display = "none";
                  }}
                />
              </motion.div>
            </div>
            <span
              className={`text-sm ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Loading review documents...
            </span>
          </div>
        </div>
      ) : reviewDocuments.length === 0 ? (
        <div className="text-center py-8">
          <Shield
            className={`mx-auto h-12 w-12 ${
              isDarkMode ? "text-gray-600" : "text-gray-400"
            } mb-4`}
          />
          <p
            className={`text-lg font-medium ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            {showReviewedDocs
              ? "No verified documents"
              : "No documents pending verification"}
          </p>
          <p
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            } mt-1`}
          >
            {showReviewedDocs
              ? "Documents you have verified will appear here"
              : "Documents requiring verification will appear here"}
          </p>
        </div>
      ) : (
        <ul className="space-y-4">
          {reviewDocuments.map((doc) => (
            <li
              key={doc.id}
              className={`rounded overflow-hidden border shadow-sm transition-all duration-300 hover:shadow-md ${
                isDarkMode
                  ? "bg-gray-700 border-slate-950"
                  : "bg-gray-100 border-slate-280"
              }`}
            >
              <div
                className={`flex flex-col border-b ${
                  isDarkMode ? "border-slate-950" : "border-slate280"
                } sm:flex-row sm:items-center sm:justify-between p-4`}
              >
                <div
                  className={`${
                    isDarkMode ? "border-slate-950" : "border-slate-280"
                  } flex items-center space-x-4 cursor-pointer flex-1`}
                  onClick={() =>
                    setExpandedReviewDocs((prev) => ({
                      ...prev,
                      [doc.id]: !prev[doc.id],
                    }))
                  }
                >
                  <div
                    className={`w-3 h-3 rounded-full ${
                      doc.low_confidence_count > 5
                        ? "bg-red-500"
                        : doc.low_confidence_count > 2
                        ? "bg-yellow-500"
                        : "bg-green-500"
                    }`}
                  ></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span
                        className={`font-medium truncate ${
                          isDarkMode ? "text-gray-200" : "text-gray-800"
                        }`}
                      >
                        {doc.name}
                      </span>
                    </div>
                    <DocumentMetaLine
                      doc={doc}
                      isDarkMode={isDarkMode}
                      dateTime={formatDateTime(doc.date, doc.time)}
                    />
                  </div>
                </div>
                <div className="flex items-center mt-2 sm:mt-0 space-x-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      doc.low_confidence_count > 5
                        ? "bg-red-100 text-red-800"
                        : doc.low_confidence_count > 2
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    Low confidence: {doc.low_confidence_count} fields
                  </span>

                  {/* Mark as Verified button - only show for non-verified documents */}
                  {!doc.isReviewed && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          window.confirm(
                            "Mark this document as verified?"
                          )
                        ) {
                          markDocumentAsVerified(doc.id);
                        }
                      }}
                      disabled={markingAsVerified[doc.id]}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 ${
                        markingAsVerified[doc.id]
                          ? "opacity-50 cursor-not-allowed"
                          : isDarkMode
                          ? "bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/30 hover:text-green-300"
                          : "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 hover:text-green-800"
                      }`}
                      title="Mark this document as verified"
                    >
                      {markingAsVerified[doc.id]
                        ? "Verifying..."
                        : "Mark as Verified"}
                    </button>
                  )}

                  {/* Open in new tab button */}
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        // Get presigned URL for the document
                        const token = localStorage.getItem("authToken");
                        const response = await axios.get(
                          `/api/document/${doc.id}/presigned_url`,
                          {
                            headers: {
                              Authorization: `Bearer ${token}`,
                            },
                          }
                        );
                        if (response.data.presigned_url) {
                          window.open(
                            response.data.presigned_url,
                            "_blank"
                          );
                        } else {
                          alert("Unable to open document");
                        }
                      } catch (error) {
                        console.error(
                          "Error getting presigned URL:",
                          error
                        );
                        alert("Error opening document");
                      }
                    }}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      isDarkMode
                        ? "text-blue-400 hover:bg-blue-500/20 hover:text-blue-300"
                        : "text-blue-600 hover:bg-blue-100 hover:text-blue-700"
                    }`}
                    title="Open in new tab"
                  >
                    <ExternalLink size={16} />
                  </button>

                  {/* Dropdown arrow */}
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      setExpandedReviewDocs((prev) => ({
                        ...prev,
                        [doc.id]: !prev[doc.id],
                      }))
                    }
                  >
                    {expandedReviewDocs[doc.id] ? (
                      <ChevronUp
                        size={20}
                        className={`${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    ) : (
                      <ChevronDown
                        size={20}
                        className={`${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded content with Data/Media toggle */}
              {expandedReviewDocs[doc.id] && (
                <DocumentExpandedView
                  doc={doc}
                  isDarkMode={isDarkMode}
                  themeClasses={themeClasses}
                  documentViewModes={documentViewModes}
                  resultViewMode={resultViewMode}
                  editingDocuments={editingDocuments}
                  savingDocuments={savingDocuments}
                  handleDocumentViewModeChange={
                    handleDocumentViewModeChange
                  }
                  handleEditDocument={handleEditDocument}
                  handleSaveDocument={handleSaveDocument}
                  handleCancelEdit={handleCancelEdit}
                  copyToClipboard={copyToClipboard}
                  downloadAsCSV={downloadAsCSV}
                  toggleResultViewMode={toggleResultViewMode}
                  MediaViewer={MediaViewer}
                  hasMediaToggle={true}
                  presignedUrls={presignedUrls}
                  loadingUrls={loadingUrls}
                  fetchPresignedUrls={fetchPresignedUrls}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ReviewSection;
